'use client'

import { ou } from '@/lib/contenuEditable'
import { NEWSLETTER_SUBSCRIBE_URL } from '@/lib/newsletter'

import { useEffect, useRef, useState } from 'react'

interface NewsletterModalProps {
  open: boolean
  onClose: () => void
  content?: NewsletterContent
}

/**
 * Contenu éditable depuis le CRM — section `newsletter` de la page d'accueil.
 *
 * Cette section existait dans la configuration sans être rendue nulle part :
 * elle promettait une modification sans effet. Elle alimente désormais cette
 * fenêtre, qui est le seul endroit où l'on s'inscrit sur le site — le
 * formulaire en pied de page, lui, n'a jamais été affiché.
 *
 * Rien ne change à l'écran : ce sont les mêmes textes, simplement déplacés
 * du code vers un endroit modifiable.
 */
export interface NewsletterContent {
  eyebrow?: string
  title?: string
  subtitle?: string
  submitLabel?: string
  successEyebrow?: string
  successTitle?: string
  successMessage?: string
  errorMessage?: string
  placeholder?: string
}

const SUBMIT_URL = NEWSLETTER_SUBSCRIBE_URL

/** Le message de confirmation tient sur deux lignes séparées par un retour. */
function lignes(texte: string) {
  const parts = texte.split('\n')
  return parts.map((t, i) => (
    <span key={i}>
      {t}
      {i < parts.length - 1 && <br />}
    </span>
  ))
}

export default function NewsletterModal({ open, onClose, content }: NewsletterModalProps) {
  const [firstName, setFirstName] = useState('')
  const [email, setEmail] = useState('')
  const [website, setWebsite] = useState('') // honeypot
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const dialogRef = useRef<HTMLDivElement>(null)
  const firstFieldRef = useRef<HTMLInputElement>(null)

  // Bloquer le scroll du body quand le modal est ouvert
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      // Focus sur le 1er champ après animation
      setTimeout(() => firstFieldRef.current?.focus(), 80)
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  // Fermer avec Escape
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (status === 'submitting') return
    if (website) return // honeypot
    setStatus('submitting')
    setErrorMsg('')
    try {
      const res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName: firstName || undefined, email, source: 'site' }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setStatus('success')
    } catch (err) {
      setStatus('error')
      setErrorMsg(err instanceof Error ? err.message : 'Erreur réseau')
    }
  }

  const handleClose = () => {
    onClose()
    // Reset à la fermeture (avec délai pour laisser l'anim finir)
    setTimeout(() => {
      setFirstName('')
      setEmail('')
      setStatus('idle')
      setErrorMsg('')
    }, 300)
  }

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="newsletter-title"
      className="fixed inset-0 z-[2000] flex items-center justify-center px-4 py-8"
      onClick={(e) => {
        if (e.target === dialogRef.current?.parentElement) handleClose()
      }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden
      />

      {/* Modal */}
      <div
        ref={dialogRef}
        className="relative w-full max-w-md bg-brand-nuit border border-brand-accent/20 px-8 py-10 md:px-10 md:py-12 shadow-2xl"
        style={{ animation: 'newsletterModalIn 0.3s ease-out' }}
      >
        <style>{`
          @keyframes newsletterModalIn {
            from { opacity: 0; transform: translateY(12px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>

        {/* Close button */}
        <button
          type="button"
          onClick={handleClose}
          aria-label="Fermer"
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-brand-cream/60 hover:text-brand-cream transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {status === 'success' ? (
          <div className="text-center py-4">
            <div className="w-12 h-px bg-brand-accent mx-auto mb-8" />
            <p className="text-[11px] tracking-[0.4em] text-brand-accent/70 uppercase mb-4">{ou(content?.successEyebrow, 'Confirmation')}</p>
            <h2 id="newsletter-title" className="font-serif text-2xl md:text-3xl text-brand-cream font-normal leading-[1.3] mb-6">
              {ou(content?.successTitle, 'Votre demande a bien été reçue')}
            </h2>
            <p className="text-sm text-brand-cream/70 leading-[1.7] font-normal mb-8">
              {lignes(ou(content?.successMessage, "Un email de confirmation vient de vous être envoyé.\nVérifiez votre boîte de réception (et vos spams au besoin)."))}
            </p>
            <button
              type="button"
              onClick={handleClose}
              className="gde-btn gde-btn--on-dark"
            >
              Fermer
            </button>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <p className="text-[11px] tracking-[0.4em] text-brand-accent/70 uppercase mb-4">{ou(content?.eyebrow, 'Newsletter')}</p>
              <h2 id="newsletter-title" className="font-serif text-2xl md:text-3xl text-brand-cream font-normal leading-[1.3]">
                {ou(content?.title, 'Rester informé')}
              </h2>
              <div className="w-10 h-px bg-brand-accent/60 mx-auto mt-6 mb-6" />
              <p className="text-sm text-brand-cream/65 leading-[1.7] font-normal">
                {ou(content?.subtitle, 'Recevez en avant-première les invitations, événements et ouvertures de billetterie du Gala des Étoiles.')}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Honeypot */}
              <input
                type="text"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                aria-hidden
              />

              <div>
                <label htmlFor="newsletter-firstName" className="block text-[10px] tracking-[0.3em] text-brand-cream/60 uppercase mb-2">
                  Prénom (optionnel)
                </label>
                <input
                  ref={firstFieldRef}
                  id="newsletter-firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full bg-transparent border-b border-brand-cream/20 px-0 py-2 text-brand-cream placeholder:text-brand-cream/30 focus:border-brand-accent focus:outline-none transition-colors text-sm"
                />
              </div>

              <div>
                <label htmlFor="newsletter-email" className="block text-[10px] tracking-[0.3em] text-brand-cream/60 uppercase mb-2">
                  Email
                </label>
                <input
                  id="newsletter-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={ou(content?.placeholder, 'vous@email.com')}
                  className="w-full bg-transparent border-b border-brand-cream/20 px-0 py-2 text-brand-cream placeholder:text-brand-cream/30 focus:border-brand-accent focus:outline-none transition-colors text-sm"
                />
              </div>

              {status === 'error' && (
                <p className="text-xs text-red-300/80 leading-relaxed pt-2">
                  {ou(content?.errorMessage, 'Une erreur est survenue. Merci de réessayer dans un instant.')}
                  {errorMsg && <span className="block opacity-50 mt-1">[{errorMsg}]</span>}
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="gde-btn gde-btn--solid mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ width: '100%' }}
              >
                {status === 'submitting' ? 'Envoi en cours…' : ou(content?.submitLabel, "M'inscrire")}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
